/**
 * Theme (00-contracts §14.1: "language is a server-side user setting; theme is local-only").
 *
 * Since Glass 2 the mode is one of four axes, owned by `AppearanceProvider` in `appearance.tsx`.
 * `useTheme()` is kept because it is used in many places and only ever needed the mode; it is a
 * view onto the same state, not a second store.
 */
import type { ReactNode } from 'react';

import { AppearanceProvider, useAppearance } from './appearance';
import { STORAGE_KEYS, THEME_MODES, type ResolvedMode, type ThemeMode } from './appearance-state';

export type ThemePreference = ThemeMode;
export type ResolvedTheme = ResolvedMode;

/**
 * The key the mode is stored under. `index.html` reads the same key from a blocking script that
 * runs before this module exists; `theme-bootstrap.spec.ts` fails if they drift apart.
 */
export const THEME_STORAGE_KEY = STORAGE_KEYS.theme;

export const THEME_PREFERENCES: readonly ThemePreference[] = THEME_MODES;

interface ThemeContextValue {
  preference: ThemePreference;
  resolved: ResolvedTheme;
  setPreference: (preference: ThemePreference) => void;
}

/** The old name for `AppearanceProvider`. */
export function ThemeProvider({ children }: { children: ReactNode }): ReactNode {
  return <AppearanceProvider>{children}</AppearanceProvider>;
}

export function useTheme(): ThemeContextValue {
  const { mode, resolvedMode, setMode } = useAppearance();
  return { preference: mode, resolved: resolvedMode, setPreference: setMode };
}
